import { useCallback, useEffect, useRef, useState } from 'react';
import {
  base64ToBlobUrl,
  postTtsVoicePreviewApi,
} from '@/shared/apis/tts/post-tts-voice-preview';

/**
 * Gemini TTS preview blob URL을 캐싱해서 반환하는 훅.
 *
 * 캐시 키에 포함하는 필드: voice / style / emotion / language / text.
 * 캐시 키에서 제외하는 필드:
 * - `tempo` — 브라우저 `audio.playbackRate`로 실시간 조절, 원본은 1.0 speed 재사용 가능.
 * - `pitch` — Preview API 스키마(`PostTtsVoicePreviewRequestSchema`)에 없음. Gemini
 *   preview는 pitch 파라미터를 받지 않으므로 키에 포함하면 의미 없는 miss 유발.
 *
 * 구분자 대신 `JSON.stringify`를 사용해 style/text에 임의 문자(`|` 등)가 들어와도
 * 키 충돌이 나지 않도록 한다.
 *
 * 단일 entry 캐시: detail 카드는 화면에 하나만 존재하는 구조라 LRU 불필요. 여러 카드가
 * 공유해야 하는 요건이 생기면 이 훅을 `TtsDialogContext`로 끌어올리면 된다.
 */
export type GeminiPreviewLanguage = 'KOREAN' | 'ENGLISH';

export interface GeminiPreviewParams {
  voice: string;
  style: string;
  emotion: string;
  language: GeminiPreviewLanguage;
  text: string;
}

export interface UseGeminiPreviewUrlResult {
  /**
   * 캐시 hit이면 기존 blob URL, miss면 API 호출 후 새 blob URL을 반환한다.
   * miss로 이전 캐시가 revoke되기 직전에 `onBeforeRevoke`가 호출되므로,
   * 동일 blob URL을 참조하던 Audio를 먼저 정지시키고 싶을 때 쓴다.
   */
  getUrl: (
    params: GeminiPreviewParams,
    onBeforeRevoke?: () => void,
  ) => Promise<string>;
  isLoading: boolean;
}

export function useGeminiPreviewUrl(): UseGeminiPreviewUrlResult {
  const cacheRef = useRef<{ key: string; url: string } | null>(null);
  // 동일 key 요청이 in-flight인 동안 두 번째 호출이 들어오면 같은 Promise를 공유.
  // (단일 entry 전략이라 여러 key 동시 in-flight는 필요 없음)
  const pendingRef = useRef<{ key: string; promise: Promise<string> } | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  // 언마운트 시 남은 blob URL 해제.
  // 재생 중인 Audio가 이 URL을 참조하고 있을 수 있으므로, 호출자는 언마운트 직전에
  // 필요시 `stopCurrentAudio()`를 먼저 실행해야 한다 (훅은 관여하지 않는다).
  useEffect(() => {
    return () => {
      if (cacheRef.current) {
        URL.revokeObjectURL(cacheRef.current.url);
        cacheRef.current = null;
      }
    };
  }, []);

  const buildKey = (p: GeminiPreviewParams) =>
    JSON.stringify([p.voice, p.style, p.emotion, p.language, p.text]);

  const getUrl = useCallback(
    async (
      params: GeminiPreviewParams,
      onBeforeRevoke?: () => void,
    ): Promise<string> => {
      const key = buildKey(params);

      // cache hit
      const cached = cacheRef.current;
      if (cached?.key === key) {
        return cached.url;
      }

      // 동일 key가 이미 in-flight면 같은 Promise 재사용 (중복 API 호출 방지)
      if (pendingRef.current?.key === key) {
        return pendingRef.current.promise;
      }

      const promise = (async () => {
        setIsLoading(true);
        try {
          const resp = await postTtsVoicePreviewApi({
            voice: params.voice,
            text: params.text,
            emotion: params.emotion,
            style: params.style || undefined,
            language: params.language,
          });
          const newUrl = base64ToBlobUrl(
            resp.data.audioBase64,
            resp.data.mime || 'audio/wav',
          );
          const prev = cacheRef.current;
          if (prev) {
            onBeforeRevoke?.();
            URL.revokeObjectURL(prev.url);
          }
          cacheRef.current = { key, url: newUrl };
          return newUrl;
        } finally {
          setIsLoading(false);
          if (pendingRef.current?.key === key) {
            pendingRef.current = null;
          }
        }
      })();

      pendingRef.current = { key, promise };
      return promise;
    },
    [],
  );

  return { getUrl, isLoading };
}
